import { useState } from "react";
import { fmtDate, maskPhone } from "../utils";
import { getSignedUrl } from "../lib/storage";
import { CONTACT_CHANNELS } from "../constants";
import { erroDeCanal } from "../lib/contatoEdit";
import { MessageSquare, ArrowRightCircle, Flag, Paperclip, EyeOff, Pencil } from "lucide-react";

const campoStyle = { display: "flex", flexDirection: "column", gap: 3, fontSize: 10, color: "#64748b", fontWeight: 600 };
const inputMini = { padding: "6px 9px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 11.5, background: "#fff", color: "#334155", fontFamily: "inherit" };

// Abre o print do contato numa aba nova. O arquivo é privado: a URL é
// assinada na hora e expira, então não dá para vazar o link por engano.
const abrirPrint = async (path) => {
  const win = window.open("", "_blank");
  const url = await getSignedUrl(path);
  if (!url) { if (win) win.close(); return; }
  if (win) win.location.href = url; else window.open(url, "_blank");
};

/**
 * Linha do tempo unificada e fiel do histórico de um cliente/parceiro.
 * Junta mudanças de estágio (stageHistory) e contatos (interactions),
 * ordena do mais recente para o mais antigo e mostra todos os detalhes.
 *
 * Com `onEditContato`, cada contato ganha o botão de corrigir o que foi
 * digitado errado — ver `lib/contatoEdit`: nome, telefone, canal, observação e
 * próximo passo. Anexo, data/hora e desfecho ficam como foram registrados.
 */
export default function HistoryTimeline({
  stageHistory = [], interactions = [], stageList = [],
  onEditContato, ehParceiro = false,
}) {
  const [editando, setEditando] = useState(null); // chave do contato em edição
  const [form, setForm] = useState({});
  const [erro, setErro] = useState("");

  const abrirEdicao = (ev) => {
    const i = ev.int || {};
    setForm({
      contactName: i.contactName || "", phoneCelular: i.phoneCelular || "",
      phoneFixo: i.phoneFixo || "", spokeWith: i.spokeWith || "",
      channel: i.channel || "", note: i.note || "",
      nextAction: i.nextAction || "", nextActionDate: i.nextActionDate || "",
    });
    setErro("");
    setEditando(ev.chave);
  };

  const salvar = (ev) => {
    const problema = erroDeCanal(ev.int || {}, form, ehParceiro);
    if (problema) { setErro(problema); return; }
    onEditContato({ id: ev.int?.id || "", idx: ev.ord }, form);
    setEditando(null);
  };
  const label = (id) => stageList.find((s) => s.id === id)?.label || id || "—";
  const color = (id) => stageList.find((s) => s.id === id)?.color || "#64748b";

  const events = [];
  (stageHistory || []).forEach((h, i) => events.push({
    date: h.date, ord: i, kind: "stage",
    title: `${h.from ? label(h.from) : "Início"} → ${label(h.to)}`,
    note: h.note || "", dot: color(h.to), icon: ArrowRightCircle,
  }));
  (interactions || []).forEach((int, i) => events.push({
    date: int.date, ord: i, kind: "contact", int, chave: int.id || `idx-${i}`,
    title: `Contato — ${int.channel || "registro"}`,
    note: int.note || "", next: int.nextAction, nextDate: int.nextActionDate,
    dot: "#0d9488", icon: MessageSquare,
    print: int.comprovantePath || "", printNome: int.comprovanteNome || "print da conversa",
    contatoEm: int.contatoEm || "", autor: int.autor || "",
    editadoPor: int.editadoPor || "", editadoEm: int.editadoEm || "",
  }));

  events.sort((a, b) => {
    const d = (b.date || "").localeCompare(a.date || "");
    return d !== 0 ? d : b.ord - a.ord;
  });

  if (!events.length) {
    return <div style={{ fontSize: 12, color: "#94a3b8", padding: "8px 0" }}>Nenhum registro ainda. Registre um contato ou movimente o estágio para começar o histórico.</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>Histórico ({events.length})</div>
      <div>
        {events.map((ev, i) => {
          const Icon = ev.icon;
          const podeCorrigir = !!onEditContato && ev.kind === "contact";
          return (
            <div key={i} style={{ display: "flex", gap: 10, position: "relative" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: ev.dot + "22", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={12} color={ev.dot} />
                </div>
                {i < events.length - 1 && <div style={{ width: 2, flex: 1, background: "#e5e7eb", minHeight: 8, margin: "2px 0" }} />}
              </div>
              <div style={{ paddingBottom: 14, flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{ev.title}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {podeCorrigir && editando !== ev.chave && (
                      <button
                        onClick={() => abrirEdicao(ev)}
                        title="Corrigir o que foi digitado neste contato"
                        style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 10, fontWeight: 600, cursor: "pointer" }}
                      >
                        <Pencil size={10} /> Editar
                      </button>
                    )}
                    <span style={{ fontSize: 10, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmtDate(ev.date)}</span>
                  </span>
                </div>
                {ev.note && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2, lineHeight: 1.4 }}>{ev.note}</div>}
                {(ev.contatoEm || ev.autor) && (
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                    {ev.contatoEm ? `${String(ev.contatoEm).slice(8, 10)}/${String(ev.contatoEm).slice(5, 7)} às ${String(ev.contatoEm).slice(11, 16)}` : ""}
                    {ev.contatoEm && ev.autor ? " · " : ""}{ev.autor}
                  </div>
                )}
                {ev.editadoEm && (
                  <div style={{ fontSize: 10, color: "#b45309", marginTop: 2 }}>
                    corrigido em {fmtDate(String(ev.editadoEm).slice(0, 10))}{ev.editadoPor ? ` por ${ev.editadoPor}` : ""}
                  </div>
                )}
                {ev.kind === "contact" && (
                  ev.print ? (
                    <button
                      onClick={() => abrirPrint(ev.print)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5, padding: "3px 9px", borderRadius: 5, border: "1px solid #99f6e4", background: "#f0fdfa", color: "#0f766e", fontSize: 10, fontWeight: 600, cursor: "pointer" }}
                    >
                      <Paperclip size={10} /> Ver print
                    </button>
                  ) : (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 5, fontSize: 10, color: "#b45309" }}>
                      <EyeOff size={10} /> sem comprovante anexado
                    </span>
                  )
                )}
                {ev.next && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#b45309", marginTop: 4 }}>
                    <Flag size={10} /> Próximo passo: {ev.next}{ev.nextDate ? ` (${fmtDate(ev.nextDate)})` : ""}
                  </div>
                )}

                {/* Correção do que foi digitado. O anexo, a data/hora e o
                    desfecho não aparecem aqui de propósito: são a prova do
                    contato, e prova não se edita depois. */}
                {editando === ev.chave && (
                  <div style={{ marginTop: 8, padding: "10px 12px", borderRadius: 9, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>Corrigir este contato</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      <label style={campoStyle}>Nome do contato
                        <input style={inputMini} value={form.contactName} onChange={(e) => setForm((p) => ({ ...p, contactName: e.target.value }))} />
                      </label>
                      <label style={campoStyle}>Canal
                        <select style={inputMini} value={form.channel} onChange={(e) => setForm((p) => ({ ...p, channel: e.target.value }))}>
                          {(CONTACT_CHANNELS.includes(form.channel) ? CONTACT_CHANNELS : [form.channel, ...CONTACT_CHANNELS]).map((ch) => (
                            <option key={ch} value={ch}>{ch || "—"}</option>
                          ))}
                        </select>
                      </label>
                      <label style={campoStyle}>Celular / WhatsApp
                        <input style={inputMini} value={form.phoneCelular} onChange={(e) => setForm((p) => ({ ...p, phoneCelular: maskPhone(e.target.value) }))} />
                      </label>
                      <label style={campoStyle}>Telefone fixo
                        <input style={inputMini} value={form.phoneFixo} onChange={(e) => setForm((p) => ({ ...p, phoneFixo: maskPhone(e.target.value) }))} />
                      </label>
                      <label style={campoStyle}>Falou com
                        <input style={inputMini} value={form.spokeWith} onChange={(e) => setForm((p) => ({ ...p, spokeWith: e.target.value }))} />
                      </label>
                      <label style={campoStyle}>Próximo passo
                        <input style={inputMini} value={form.nextAction} onChange={(e) => setForm((p) => ({ ...p, nextAction: e.target.value }))} />
                      </label>
                      <label style={campoStyle}>Data do próximo passo
                        <input type="date" style={inputMini} value={form.nextActionDate} onChange={(e) => setForm((p) => ({ ...p, nextActionDate: e.target.value }))} />
                      </label>
                      <label style={{ ...campoStyle, gridColumn: "1 / -1" }}>Observação
                        <textarea style={{ ...inputMini, minHeight: 54, resize: "vertical" }} value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
                      </label>
                    </div>
                    {erro && <div style={{ fontSize: 10.5, color: "#b91c1c", marginTop: 8, lineHeight: 1.4 }}>{erro}</div>}
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                      <button onClick={() => setEditando(null)} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
                      <button onClick={() => salvar(ev)} style={{ padding: "6px 12px", borderRadius: 6, border: "none", background: "#0d9488", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Salvar correção</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
